import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormGroup, Form, Input, Label, Button, Spinner } from "reactstrap";
import styles from "./ValidUpdateProfileForm.module.css";
import UserContext from "../auth/UserContext";
import ValetApi from "../api/Api";
// import { formatPhone } from "../utility/formatPhone";
import PhoneInput from "../components/PhoneInput";

const ValidUpdateProfileForm = () => {
    const history = useHistory();
    const { userDetail, updateUser } = useContext(UserContext);
    const canManageAssignment = Number(userDetail?.roleId) >= 3;
    const [locations, setLocations] = useState([]);
    const [podiums, setPodiums] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchFormData() {
            try {
                const res = await ValetApi.getAllLocations();
                const podiumsRes = await ValetApi.getAllPodiums();
                setPodiums(podiumsRes.data.podiums);
                setLocations(res.data.locations);
            } catch (err) {
                console.error("Error fetching data", err);
            } finally {
                setLoading(false);
            }
        }
        fetchFormData();
    }, []);

    const formik = useFormik({
        initialValues: {
            username: userDetail.username,
            password: "",
            firstName: userDetail.firstName,
            lastName: userDetail.lastName,
            email: userDetail.email,
            phone: userDetail.phone,
            locationId: userDetail.locationId || "",
            podiumId: userDetail.podiumId || "",
        },
        validationSchema: Yup.object({
            username: Yup.string().max(25, "Must be 25 characters or less").required("username required"),
            password: Yup.string(), // Optional for update
            firstName: Yup.string().required("firstName required"),
            lastName: Yup.string().required("lastName required"),
            email: Yup.string().required("email required"),
            phone: Yup.string().required("phone required"),
            locationId: Yup.number(),
            podiumId: Yup.number(),
        }),
        onSubmit: async (values) => {
            // Only send the fields the backend expects
            const { firstName, lastName, email, phone, podiumId, password } = values;
            const updateData = {
                firstName,
                lastName,
                email,
                phone,
            };
            if (canManageAssignment) updateData.podiumId = Number(podiumId);
            if (password) updateData.password = password;
            await updateUser(userDetail.username, updateData);
            history.push("/updateProfile");
        },
    });

    const visibleFieldKeys = canManageAssignment ? Object.keys(formik.initialValues) : Object.keys(formik.initialValues).filter((key) => key !== "locationId" && key !== "podiumId");

    // Filter podiums based on selected locationId
    const filteredPodiums = formik.values.locationId ? podiums.filter((p) => String(p.locationId) === String(formik.values.locationId)) : [];

    // Reset podiumId when locationId changes
    useEffect(() => {
        formik.setFieldValue("podiumId", "");
        // eslint-disable-next-line
    }, [formik.values.locationId]);

    if (loading) {
        return (
            <div className={styles.formWrapper}>
                <Spinner
                    style={{ height: `3rem`, width: `3rem` }}
                    type="grow"
                    color="primary"
                />
            </div>
        );
    }

    // --- Render functions (copied from ValidRegisterForm) ---
    const renderTextField = (val) => (
        <Input
            id={val}
            type="text"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderUsernameField = (val) => (
        <Input
            id={val}
            type="text"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="username"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
            className="bg-light text-muted input-disabled" // Add these classes
            disabled // Username should not be editable on update
        />
    );

    const renderNewPasswordField = (val) => (
        <Input
            id={val}
            type="password"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="new-password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderEmailField = (val) => (
        <Input
            id={val}
            type="email"
            name={val}
            placeholder={val.toLowerCase()}
            value={formik.values[val]}
            autoComplete="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched[val] && !!formik.errors[val]}
        />
    );

    const renderPhoneField = (val) => (
        <PhoneInput
            id={val}
            name={val}
            value={formik.values[val]} // store raw digits
            onChange={(raw) => formik.setFieldValue(val, raw)} // keep only digits in form state
            invalid={formik.touched[val] && !!formik.errors[val]}
            autoComplete="tel"
        />
        // <Input
        //     id={val}
        //     type="tel"
        //     name={val}
        //     placeholder={val.toLowerCase()}
        //     value={formik.values[val]}
        //     autoComplete="tel"
        //     onChange={formik.handleChange}
        //     onBlur={formik.handleBlur}
        //     invalid={formik.touched[val] && !!formik.errors[val]}
        // />
    );

    const renderLocationField = (val) =>
        locations.length >= 1 ? (
            <Input
                id={val}
                type="select"
                name={val}
                value={formik.values[val]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[val] && !!formik.errors[val]}
                disabled={!canManageAssignment}
                className={!canManageAssignment ? "input-disabled" : ""}>
                <option value="">Select Location</option>
                {locations.map((location) => (
                    <option
                        key={location.id}
                        value={location.id}>
                        {location.name}
                    </option>
                ))}
            </Input>
        ) : (
            <Spinner
                style={{ height: `3rem`, width: `3rem` }}
                type="grow"
                color="primary"
            />
        );

    const renderPodiumField = (val) =>
        locations.length < 1 ? (
            <Spinner
                style={{ height: `3rem`, width: `3rem` }}
                type="grow"
                color="primary"
            />
        ) : (
            <Input
                id={val}
                type="select"
                name={val}
                value={formik.values[val]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched[val] && !!formik.errors[val]}
                disabled={!canManageAssignment || !formik.values.locationId}
                className={!canManageAssignment || !formik.values.locationId ? "input-disabled" : ""}>
                <option value="">Select Podium</option>
                {filteredPodiums.map((podium) => (
                    <option
                        key={podium.id}
                        value={podium.id}>
                        {podium.name}
                    </option>
                ))}
            </Input>
        );

    const renderInputField = (val) => {
        switch (val) {
            case "username":
                return renderUsernameField(val);
            case "firstName":
            case "lastName":
                return renderTextField(val);
            case "password":
                return renderNewPasswordField(val);
            case "email":
                return renderEmailField(val);
            case "phone":
                return renderPhoneField(val);
            case "locationId":
                return renderLocationField(val);
            case "podiumId":
                return renderPodiumField(val);
            default:
                return renderTextField(val);
        }
    };

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner">
                <div className={styles.formWrapper}>
                    <Form
                        onSubmit={formik.handleSubmit}
                        className={styles.updateForm}>
                        <h3 className={styles.formTitle}>{userDetail?.username || ""} Profile</h3>
                        {visibleFieldKeys.map((val, idx) => (
                            <FormGroup
                                key={`FormGroup-${val}`}
                                className={styles.formGroup}>
                                {renderInputField(val)}
                                <Label htmlFor={val}>{val.charAt(0).toUpperCase() + val.slice(1)}</Label>
                                {formik.touched[val] && formik.errors[val] && <div className="text-danger">{formik.errors[val]}</div>}
                            </FormGroup>
                        ))}
                        <Button
                            className={styles.updateBtn}
                            type="submit">
                            Update
                        </Button>
                    </Form>
                </div>
            </div>
        </section>
    );
};

export default ValidUpdateProfileForm;
